import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { ApiError, api, useAuth } from '@forbiz/web-kit'
import { createOrderRequest, type OrderDetailResponse, type OrderTransitionRequest, type PhotoView } from '@forbiz/contracts'
import { TRANSITIONS, type OrderStatus, type Transition } from '@forbiz/spec'

import { BottomSheet } from '../components/BottomSheet.js'
import { openPostcodeSearch } from '../lib/daumPostcode.js'
import { getWreathDesign } from '../lib/digitalWreath.js'
import { buildOrderBody, orderToForm, type FormState } from '../lib/orderForm.js'
import { ordersApi } from '../lib/orders.js'
import { paymentsApi } from '../lib/payments.js'
import { photosApi } from '../lib/photos.js'
import { createReorderDraft } from '../lib/reorder.js'
import { isTossUserCancel, startTossPayment } from '../lib/toss.js'
import { useAsync } from '../lib/useAsync.js'
import styles from '../app.module.css'

/**
 * 기업 주문 상세(BIZ-10). 역할별로 채워지는 필드가 다르다(서버가 비움) —
 * null 필드는 줄을 렌더하지 않고, 값이 하나도 없는 섹션은 통째로 숨긴다.
 * 상태 라벨은 항상 채워지는 companyFacingStatus를 쓴다.
 */

const occasionLabel: Record<OrderDetailResponse['occasionType'], string> = {
  FUNERAL: '근조',
  CELEBRATION: '축하',
  GIFT: '난·기타',
}

const occasionClass: Record<OrderDetailResponse['occasionType'], string | undefined> = {
  FUNERAL: styles.occFuneral,
  CELEBRATION: styles.occCelebrate,
  GIFT: styles.occGift,
}

const timeSlotLabel: Record<string, string> = {
  BEFORE_12: '12시 이전',
  BEFORE_14: '14시 이전',
  BEFORE_16: '16시 이전',
  ANYTIME: '시간 무관',
}

/** 업체 전이 버튼 문구(도착 상태 기준의 자연스러운 동작 라벨). */
const VENDOR_TRANSITION_LABEL: Partial<Record<OrderStatus, string>> = {
  VENDOR_ACCEPTED: '주문 접수',
  VENDOR_REJECTED: '처리 불가',
  PREPARING: '상품 준비',
  DELIVERING: '배송 시작',
  DELIVERED: '배송 완료 처리',
  DELIVERY_ISSUE: '배송 문제 등록', // DELIVERING에서 업체도 갈 수 있는 전이 — 라벨 누락 시 raw enum이 노출된다
}

/** 사진을 올릴 수 있는(=배송 단계) 내부 상태. */
const PHOTO_UPLOAD_STATES: readonly OrderStatus[] = ['DELIVERING', 'DELIVERED', 'DELIVERY_ISSUE']

function statusChipClass(status: string): string | undefined {
  if (status.includes('취소') || status.includes('문제')) return styles.chipDanger
  if (status.includes('완료')) return styles.chipSuccess
  if (status.includes('확인 필요')) return styles.chipWarning
  return styles.chipInfo
}

function fmtDate(s: string | null): string {
  if (!s) return ''
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('ko-KR')
}

function fmtDateTime(s: string | null): string {
  if (!s) return ''
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('ko-KR')
}

function fmtWon(n: number | null): string {
  return n === null ? '' : `${n.toLocaleString('ko-KR')}원`
}

// ── 섹션 아이콘 ────────────────────────────────────────────────
const s = 1.8
const ICON = {
  delivery: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 21s-7-5.2-7-11a7 7 0 0 1 14 0c0 5.8-7 11-7 11z" stroke="currentColor" strokeWidth={s} />
      <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth={s} />
    </svg>
  ),
  funeral: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth={s} />
      <path d="M4 9h16M9 3v4M15 3v4" stroke="currentColor" strokeWidth={s} strokeLinecap="round" />
    </svg>
  ),
  celebration: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 21 8 9l7 7-10 5zM8 9l4-4 4 4M13 5l3-3" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
    </svg>
  ),
  gift: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 8h16v4H4zM6 12v9h12v-9M12 8v13" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
      <path d="M12 8S9 3 6.5 4.5 8 8 12 8zm0 0s3-5 5.5-3.5S16 8 12 8z" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
    </svg>
  ),
  ribbon: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6v10a2 2 0 0 0 2 2h11l3 3V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
    </svg>
  ),
  memo: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 4h9l5 5v11H5zM14 4v5h5" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
      <path d="M8 13h8M8 16h5" stroke="currentColor" strokeWidth={s} strokeLinecap="round" />
    </svg>
  ),
  receipt: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth={s} />
      <path d="m3 8 9 5 9-5" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
    </svg>
  ),
  product: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 7h16v13H4zM8 7V4h8v3" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
    </svg>
  ),
  supply: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 13h11l2-4h4l1 4v3h-2M3 16h1M6 16h10" stroke="currentColor" strokeWidth={s} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="6" cy="18" r="1.6" stroke="currentColor" strokeWidth={s} />
      <circle cx="17" cy="18" r="1.6" stroke="currentColor" strokeWidth={s} />
    </svg>
  ),
  photo: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="15" rx="2" stroke="currentColor" strokeWidth={s} />
      <circle cx="8.5" cy="10.5" r="1.8" stroke="currentColor" strokeWidth={s} />
      <path d="m4 18 5-4 4 3 3-2 5 4" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
    </svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth={s} strokeLinecap="round" />
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth={s} />
    </svg>
  ),
  // 디지털 카드 — 카드(사각) + 마음(하트). 이모지 💌 대체.
  card: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth={s} />
      <path d="M12 15.5c-2-1.3-3-2.4-3-3.6a1.5 1.5 0 0 1 3-.5 1.5 1.5 0 0 1 3 .5c0 1.2-1 2.3-3 3.6z" stroke="currentColor" strokeWidth={s} strokeLinejoin="round" />
    </svg>
  ),
  // 디지털 화환 — 4잎 국화(라인). 이모지 🏵️ 대체, 근조 절제 톤.
  wreath: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="2.4" stroke="currentColor" strokeWidth={s} />
      <path
        d="M12 3.8c2.1 1.3 2.1 4.3 0 5.6-2.1-1.3-2.1-4.3 0-5.6zM12 20.2c-2.1-1.3-2.1-4.3 0-5.6 2.1 1.3 2.1 4.3 0 5.6zM3.8 12c1.3-2.1 4.3-2.1 5.6 0-1.3 2.1-4.3 2.1-5.6 0zM20.2 12c-1.3 2.1-4.3 2.1-5.6 0 1.3-2.1 4.3-2.1 5.6 0z"
        stroke="currentColor"
        strokeWidth={s}
        strokeLinejoin="round"
      />
    </svg>
  ),
} as const

const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m5 13 4 4 10-10" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// 편집 어포던스 — 값을 탭하면 그 필드를 고친다. 화살표(›)는 '이동'처럼 읽혀 수정임을
// 알기 어려워 연필 아이콘으로 바꾼다(BIZ-76 인라인 수정).
const EDIT_ICON = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.83l-1.17-1.17a2 2 0 0 0-2.83 0L4 16v4z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="m13.5 6.5 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

// ── 인라인 수정(BIZ-76) ────────────────────────────────────────
// 상세에서 값을 탭하면 그 필드만 바텀시트로 고친다. 저장은 현재 본문 전체에 그 필드만
// 덮어써서 기존 amendBody(/body)로 보낸다(새 API 없음). 어떤 필드가 편집 가능한지는
// 서버 플래그(canCompanyEdit·canCompanyEditTextOnly)로 판정한다.

/** 바텀시트로 고칠 수 있는 필드. 상품·수량은 여기 없다(마법사에서만) — 카드 메시지·리본2(발신자 파생)도 제외. */
type EditFieldId =
  | 'partnerName' | 'address' | 'desiredDate' | 'recipientPhone' | 'deliveryNote'
  | 'mortuaryRoom' | 'deceasedName' | 'mournerName'
  | 'eventType' | 'eventName' | 'eventTarget'
  | 'recipientName' | 'recipientOrg' | 'recipientDept' | 'recipientTitle'
  | 'ribbonLine1' | 'senderName' | 'companyMemo'

/** 바텀시트 제목(= 편집 대상 이름). */
const SHEET_LABEL: Record<EditFieldId, string> = {
  partnerName: '거래처',
  address: '배송지',
  desiredDate: '희망 배송일',
  recipientPhone: '수령인 연락처',
  deliveryNote: '배송 요청사항',
  mortuaryRoom: '빈소',
  deceasedName: '고인명',
  mournerName: '상주명',
  eventType: '행사 종류',
  eventName: '행사명',
  eventTarget: '행사 대상자',
  recipientName: '수령인명',
  recipientOrg: '수령처',
  recipientDept: '부서',
  recipientTitle: '직함',
  ribbonLine1: '리본 문구',
  senderName: '보내는 사람',
  companyMemo: '내부 메모',
}

type Row = [label: string, value: ReactNode, editId?: EditFieldId]

/**
 * 디지털 화환이 부고장에 어디까지 갔는지 한 마디로(BIZ-125). 실물 배송 완료 때 함께 올라간다.
 * 기업 뷰는 실패를 '준비 중'으로 접어 받는다
 * — 재시도·재송출은 운영자 몫이라 고객이 할 일은 없다. 상태가 바뀔 때만 살짝 페이드(key로 재마운트).
 */
function WreathDispatchStatus({
  dispatch,
  cancelled,
}: {
  dispatch: OrderDetailResponse['digitalWreathDispatch'] | null
  cancelled: boolean
}) {
  let text: string
  let live = false
  if (!dispatch) text = cancelled ? '게시 안 함' : '배송 완료되면 부고장에 올라가요'
  else if (dispatch.status === 'CANCELLED') text = dispatch.publishedAt ? '부고장에서 내렸어요' : '게시 안 함'
  else if (dispatch.op === 'RETRACT') text = '부고장에서 내리는 중'
  else if (dispatch.status === 'SENT') {
    text = '부고장에 올라갔어요'
    live = true
  } else text = dispatch.publishedAt ? '바뀐 문구로 고치는 중' : '부고장에 올리는 중'
  return (
    <span key={text} className={`${styles.addonStatus} ${live ? styles.addonStatusLive : ''}`}>
      {text}
    </span>
  )
}

function isPresent(v: ReactNode): boolean {
  return v !== null && v !== undefined && v !== ''
}

/** 카드 헤더 — admin과 같은 색 아이콘 칩 + 라벨(+ 우측 액션). */
function CardTitle({ icon, children, right }: { icon: ReactNode; children: ReactNode; right?: ReactNode }) {
  return (
    <h2 className={styles.infoCardTitle}>
      <span className={styles.infoCardIcon}>{icon}</span>
      {children}
      {right ? <span style={{ marginLeft: 'auto' }}>{right}</span> : null}
    </h2>
  )
}

/**
 * 정의목록 카드. 값이 있는 행만 렌더하고, 전부 비면 카드 자체를 숨긴다.
 * editable이면 편집 가능한 행(editId 有)의 값 셀을 탭 가능한 버튼으로 바꾼다(→ 바텀시트).
 */
function Section({
  icon,
  title,
  rows,
  editable,
  onEdit,
}: {
  icon: ReactNode
  title: string
  rows: Row[]
  editable?: boolean
  onEdit?: (id: EditFieldId) => void
}) {
  const present = rows.filter(([, v]) => isPresent(v))
  if (present.length === 0) return null
  return (
    <section className={styles.infoCard}>
      <CardTitle icon={icon}>{title}</CardTitle>
      <dl className={`${styles.dl} ${styles.dlTight}`}>
        {present.map(([label, value, editId]) => (
          <div key={label} className={styles.dlRow}>
            <dt className={styles.dt}>{label}</dt>
            <dd className={styles.dd}>
              {editable && onEdit && editId ? (
                <button
                  type="button"
                  className={styles.ddEdit}
                  onClick={() => onEdit(editId)}
                  aria-label={`${label} 수정`}
                >
                  <span>{value}</span>
                  {EDIT_ICON}
                </button>
              ) : (
                value
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

// ── 진행 타임라인 ──────────────────────────────────────────────

const TRACK_STEPS = ['주문 접수', '준비 중', '배송 중', '배송 완료'] as const

type TrackTone = 'normal' | 'warn' | 'danger'
interface TrackState {
  index: number
  tone: TrackTone
  complete: boolean
  note?: string
}

/**
 * 기업 노출 상태(companyFacingStatus)를 4단계 진행바로 매핑한다.
 * 기업 응답엔 내부 status가 없어(파생 상태만) 이 라벨이 유일하게 믿을 값이다.
 * 예외 상태(확인 필요·배송 문제)는 단계 위에 경고로 얹고, 작성 중·취소는 바를 안 그린다.
 */
function trackFromStatus(status: string): TrackState | null {
  switch (status) {
    case '주문 접수':
      return { index: 0, tone: 'normal', complete: false }
    case '확인 필요':
      return {
        index: 0,
        tone: 'warn',
        complete: false,
        note: '주문 내용에 확인이 필요해 담당자가 연락드릴 수 있어요. 급하시면 고객센터로 문의해 주세요.',
      }
    case '준비 중':
      return { index: 1, tone: 'normal', complete: false }
    case '배송 중':
      return { index: 2, tone: 'normal', complete: false }
    case '배송 문제':
      // 배송 사고(DELIVERY_ISSUE) 전용 라벨(BIZ-118). 업체 거절(VENDOR_REJECTED)은 기업에게
      // '준비 중'으로 흡수돼 여기 안 온다 — 재배정은 운영자가 뒤에서 처리하는 내부 churn.
      return {
        index: 2,
        tone: 'danger',
        complete: false,
        note: '배송 중 문제가 확인돼 상황을 파악하고 있어요. 확인되는 대로 연락드립니다.',
      }
    case '배송 완료':
      return { index: 3, tone: 'normal', complete: true }
    default:
      return null // 작성 중, 취소 완료, 알 수 없는 값
  }
}

function ProgressTrack({ status }: { status: string }) {
  const t = trackFromStatus(status)
  if (!t) return null

  return (
    <div className={styles.track}>
      <div className={styles.steps}>
        {TRACK_STEPS.map((label, i) => {
          const done = i < t.index || (t.complete && i <= t.index)
          const current = i === t.index && !t.complete
          const alert = current && t.tone !== 'normal'

          let stepClass = styles.step
          if (done) stepClass += ` ${styles.stepDone}`
          else if (alert) stepClass += ` ${styles.stepAlert}${t.tone === 'danger' ? ` ${styles.stepAlertDanger}` : ''}`
          else if (current) stepClass += ` ${styles.stepCurrent}`

          return (
            <div key={label} className={stepClass}>
              <div className={styles.stepDot}>{done ? CHECK : i + 1}</div>
              <div className={styles.stepCaption}>{label}</div>
            </div>
          )
        })}
      </div>
      {t.note ? (
        <p className={`${styles.trackNote} ${t.tone === 'danger' ? styles.trackNoteDanger : styles.trackNoteWarn}`}>
          {t.note}
        </p>
      ) : null}
      {/* 예외 상태의 탈출구 — '확인하러 왔는데 할 게 없다'로 끝나지 않게 연락 액션을 준다. */}
      {t.tone !== 'normal' ? <CallCsLink /> : null}
    </div>
  )
}

/** 고객센터 전화 링크. 상품·수량 변경·취소처럼 화면에서 못 하는 요청은 전화로.
 * block=true면 다른 액션 버튼과 폭을 맞춘 풀폭 버튼, 아니면 인라인 pill. */
function CallCsLink({ block }: { block?: boolean }) {
  return (
    <a href="tel:1688-4318" className={block ? `${styles.trackCall} ${styles.callBlock}` : styles.trackCall}>
      <svg
        viewBox="0 0 24 24"
        width="15"
        height="15"
        fill="none"
        aria-hidden="true"
        style={{ verticalAlign: '-2px', marginRight: 5 }}
      >
        <path
          d="M6 3h3l1.5 5-2 1.5a11 11 0 0 0 5 5l1.5-2 5 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 4 5a2 2 0 0 1 2-2z"
          stroke="currentColor"
          strokeWidth={s}
          strokeLinejoin="round"
        />
      </svg>
      고객센터 1688-4318
    </a>
  )
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const orderId = Number(id)

  // 접수·결제 직후 착지 피드백(1회성) — 주문서/결제 성공 페이지가 라우터 state로 알린다.
  // 얼럿·모달 대신 착지 배너: 흐름을 막지 않고, 근조 맥락이라 차분한 확인 톤을 유지한다.
  const arrival = location.state as { submitted?: boolean; paid?: boolean; amended?: boolean } | null
  const [justDone] = useState<'submitted' | 'paid' | 'amended' | null>(
    arrival?.paid ? 'paid' : arrival?.amended ? 'amended' : arrival?.submitted ? 'submitted' : null,
  )
  useEffect(() => {
    // state를 소진해 새로고침·뒤로가기 복귀 때 배너가 다시 뜨지 않게 한다.
    if (arrival) navigate(location.pathname, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, loading, error, reload, mutate } = useAsync(() => ordersApi.detail(orderId), [id])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  // 인라인 수정(BIZ-76): 열려 있는 바텀시트의 편집 대상 필드.
  const [editing, setEditing] = useState<EditFieldId | null>(null)
  // 재주문(BIZ-80).
  const [reordering, setReordering] = useState(false)
  const [reorderError, setReorderError] = useState('')

  const companyId = user?.companyId ?? null

  async function submitDraft() {
    setSubmitError('')
    setSubmitting(true)
    try {
      await ordersApi.submit(orderId)
      reload() // 접수 후 발번·상태 반영
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : '접수하지 못했습니다')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <main>
        <p className={styles.muted}>불러오는 중…</p>
      </main>
    )
  }

  if (error || !data) {
    return (
      <main>
        <div className={styles.header}>
          <h1 className={styles.title}>주문을 찾을 수 없습니다</h1>
        </div>
        <div className={styles.error} role="alert">
          {error ?? '요청한 주문 정보를 불러오지 못했습니다.'}
        </div>
        <Link to="/orders" className={styles.link}>
          ← 주문 목록으로
        </Link>
      </main>
    )
  }

  const order = data.order
  const isCompany = user?.role === 'COMPANY_ADMIN' || user?.role === 'COMPANY_MEMBER'
  const isVendor = user?.role === 'VENDOR_MEMBER'

  // 인라인 수정 가능 여부: 접수된(주문번호 有) 기업 주문이고, 서버가 편집 허용(발주 전 전체·발주~준비 중 텍스트)이며,
  // 본문 재조립에 필요한 상품 스냅샷이 있을 때. 상품·수량 변경은 여기 없다(마법사 몫).
  const canEdit =
    isCompany &&
    order.orderNo !== null &&
    order.item !== null &&
    Boolean(order.canCompanyEdit || order.canCompanyEditTextOnly)
  // 재주문은 접수된(주문번호·상품 有) 기업 주문이면 상태와 무관하게 가능(완료·취소 포함).
  const canReorder = isCompany && order.orderNo !== null && order.item !== null

  async function reorder() {
    if (companyId == null) return
    setReordering(true)
    setReorderError('')
    try {
      const newId = await createReorderDraft(companyId, order)
      navigate(`/orders/${newId}/edit`)
    } catch (err) {
      setReorderError(err instanceof ApiError ? err.message : '다시 주문을 시작하지 못했습니다')
      setReordering(false)
    }
  }

  const addressLine = order.deliveryAddress
    ? `${order.deliveryAddress}${order.deliveryAddressDetail ? ` ${order.deliveryAddressDetail}` : ''}`
    : null
  const timeSlot = order.desiredTimeSlot
    ? (timeSlotLabel[order.desiredTimeSlot] ?? order.desiredTimeSlot)
    : null

  return (
    <main>
      <div style={{ marginBottom: 'var(--space-sm)' }}>
        <Link to="/orders" className={styles.link}>
          ← 주문 목록으로
        </Link>
      </div>

      {/* 접수·결제 직후 1회성 완료 배너 */}
      {justDone ? (
        <div className={styles.doneBanner} role="status">
          <span className={styles.doneCheck} aria-hidden="true">
            ✓
          </span>
          {justDone === 'paid'
            ? '결제가 완료됐어요 — 주문 준비를 시작할게요.'
            : justDone === 'amended'
              ? '수정 내용이 저장됐어요.'
              : '주문이 접수됐어요 — 준비가 시작되면 알려드릴게요.'}
        </div>
      ) : null}

      <div className={styles.header}>
        <h1 className={styles.title} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          {order.orderNo ?? '임시저장'}
          <span className={`${styles.occ} ${occasionClass[order.occasionType]}`}>
            {occasionLabel[order.occasionType]}
          </span>
        </h1>
        <span className={`${styles.chip} ${statusChipClass(order.companyFacingStatus)}`}>
          {order.companyFacingStatus}
        </span>
      </div>

      <ProgressTrack status={order.companyFacingStatus} />

      {/* 결제 (BIZ-78): 가승인(선불만) 기업은 결제 완료 전까지 배너로 유도. 서버가 판정(paymentRequired). */}
      {isCompany && order.orderNo !== null ? <PaymentBanner order={order} /> : null}
      {isCompany &&
      (((order.item?.discount ?? 0) > 0 && order.pointOption !== 'REDEEM') ||
        order.pointsEarned ||
        order.pointsEarnPending ||
        order.pointsRedeemed) ? (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-sm)',
            flexWrap: 'wrap',
            margin: 'var(--space-sm) 0',
            padding: '12px 16px',
            borderRadius: 'var(--round-card)',
            background: 'var(--surface-feature)',
            fontSize: 14,
            color: 'var(--green-dark)',
            fontWeight: 600,
          }}
        >
          {order.pointsRedeemed ? (
            <span>포인트 {order.pointsRedeemed.toLocaleString('ko-KR')}P로 전액 결제</span>
          ) : (order.item?.discount ?? 0) > 0 ? (
            <span>{order.item!.discount.toLocaleString('ko-KR')}원 할인</span>
          ) : null}
          {order.pointsEarned ? <span>포인트 {order.pointsEarned.toLocaleString('ko-KR')}P 적립</span> : null}
          {order.pointsEarnPending ? (
            <span>배송 완료 시 {order.pointsEarnPending.toLocaleString('ko-KR')}P 적립 예정</span>
          ) : null}
        </div>
      ) : null}

      {/* 이 주문에 포함된 디지털 옵션(BIZ-86/126) — 풀 문장 설명 대신 '뭐가 포함' 한 줄로 간결하게. */}
      {isCompany && (order.digitalCardSkinId != null || order.digitalWreathDesign) ? (
        <div className={styles.addons}>
          {order.digitalCardSkinId != null ? (
            <div className={styles.addonRow}>
              <span className={styles.addonIcon} aria-hidden="true">{ICON.card}</span>
              <span className={styles.addonLabel}>디지털 카드</span>
              {order.digitalCardToken ? (
                <a
                  href={`/card/${order.digitalCardToken}`}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.addonAction}
                >
                  카드 보기 →
                </a>
              ) : (
                <span className={styles.addonSub}>배송 완료 후 발송</span>
              )}
            </div>
          ) : null}
          {order.digitalWreathDesign ? (
            <div className={styles.addonRow}>
              <span className={styles.addonIcon} aria-hidden="true">{ICON.wreath}</span>
              <span className={styles.addonLabel}>디지털 화환</span>
              <span className={styles.addonSub}>{getWreathDesign(order.digitalWreathDesign).name}</span>
              <WreathDispatchStatus dispatch={order.digitalWreathDispatch ?? null} cancelled={order.companyFacingStatus === '취소 완료'} />
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.infoGrid}>
        <Section
          icon={ICON.delivery}
          title="배송 정보"
          editable={canEdit}
          onEdit={setEditing}
          rows={[
            ['거래처', order.partnerName, 'partnerName'],
            ['배송지', addressLine, 'address'],
            ['장소', order.deliveryPlaceName, 'address'],
            ['희망일', fmtDate(order.desiredDate), 'desiredDate'],
            ['희망 시간대', timeSlot],
            ['수령인 연락처', order.recipientPhone, 'recipientPhone'],
            ['배송 메모', order.deliveryNote, 'deliveryNote'],
          ]}
        />

        {order.occasionType === 'FUNERAL' ? (
          <Section
            icon={ICON.funeral}
            title="근조 정보"
            editable={canEdit}
            onEdit={setEditing}
            rows={[
              ['빈소', order.mortuaryRoom, 'mortuaryRoom'],
              ['고인명', order.deceasedName, 'deceasedName'],
              ['상주명', order.mournerName, 'mournerName'],
            ]}
          />
        ) : null}

        {order.occasionType === 'CELEBRATION' ? (
          <Section
            icon={ICON.celebration}
            title="축하 정보"
            editable={canEdit}
            onEdit={setEditing}
            rows={[
              ['행사 종류', order.eventType, 'eventType'],
              ['행사명', order.eventName, 'eventName'],
              ['행사 대상', order.eventTarget, 'eventTarget'],
            ]}
          />
        ) : null}

        {order.occasionType === 'GIFT' ? (
          <Section
            icon={ICON.gift}
            title="수령 정보"
            editable={canEdit}
            onEdit={setEditing}
            rows={[
              ['수령인', order.recipientName, 'recipientName'],
              ['수령처', order.recipientOrg, 'recipientOrg'],
              ['부서', order.recipientDept, 'recipientDept'],
              ['직함', order.recipientTitle, 'recipientTitle'],
            ]}
          />
        ) : null}

        <Section
          icon={ICON.ribbon}
          title="문구"
          editable={canEdit}
          onEdit={setEditing}
          rows={[
            // 리본 아랫줄(리본 문구 2)은 발신자에서 파생돼 '발신자' 행과 값이 겹친다 —
            // 중복 노출을 없애고 리본 윗줄 문구 + 발신자만 보여준다(발신자를 고치면 리본 2도 따라 바뀐다).
            ['리본 문구', order.ribbonLine1, 'ribbonLine1'],
            ['카드 메시지', order.cardMessage],
            ['발신자', order.senderName, 'senderName'],
          ]}
        />

        <Section
          icon={ICON.memo}
          title="메모"
          editable={canEdit}
          onEdit={setEditing}
          rows={[
            ['기업 메모', order.companyMemo, 'companyMemo'],
            ['업체 메모', order.vendorMemo],
          ]}
        />

        <Section
          icon={ICON.receipt}
          title="접수 정보"
          rows={[
            ['등록일시', fmtDateTime(order.createdAt)],
            ['접수일시', fmtDateTime(order.submittedAt)],
          ]}
        />
      </div>

      {order.item ? (
        <section className={styles.infoCard} style={{ marginTop: 'var(--space-lg)' }}>
          <CardTitle icon={ICON.product}>상품</CardTitle>
          {/* 화환 주문은 항상 상품 1건 — 테이블(빈 3열)은 얼빵해 영수증 한 줄로 보여준다. */}
          <div className={styles.productLine}>
            <div className={styles.productName}>{order.item.productName}</div>
            <div className={styles.productMeta}>
              <span className={styles.productQty}>수량 {order.item.quantity.toLocaleString('ko-KR')}개</span>
              <span className={styles.productAmount}>{fmtWon(order.item.totalAmount)}</span>
            </div>
          </div>
        </section>
      ) : null}

      {/* 공급 정보(공급가=포비즈 원가)는 절대 기업에 노출 금지. 백엔드가 기업 응답에 supply를
          안 채우는 게 진짜 방어선이고, 여기 !isCompany 게이트는 회귀 대비 2차 방어다(BIZ-32 #1). */}
      {!isCompany && order.supply ? (
        <section className={styles.section}>
          <CardTitle icon={ICON.supply}>공급 정보</CardTitle>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">업체 상품명</th>
                  <th scope="col" className={styles.num}>
                    수량
                  </th>
                  <th scope="col" className={styles.num}>
                    공급 단가
                  </th>
                  <th scope="col" className={styles.num}>
                    합계
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{order.supply.vendorProductName}</td>
                  <td className={styles.num}>{order.supply.quantity.toLocaleString('ko-KR')}</td>
                  <td className={styles.num}>{fmtWon(order.supply.supplyUnitPrice)}</td>
                  <td className={styles.num}>{fmtWon(order.supply.totalPayable)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <PhotosSection orderId={orderId} order={order} isVendor={isVendor} />

      <HistorySection orderId={orderId} />

      {/* 업체는 배정 주문의 상태를 여기서 전이한다(허용 전이는 상태머신이 판정). */}
      {isVendor && order.status ? (
        <VendorActionPanel order={order} onApplied={(o) => mutate({ order: o })} />
      ) : null}

      {/* 임시저장(주문번호 없음)은 기업이 여기서 접수할 수 있다. */}
      {isCompany && order.orderNo === null ? (
        <div className={styles.card} style={{ marginTop: 'var(--space-xl)' }}>
          <p className={styles.muted} style={{ marginBottom: 'var(--space-sm)' }}>
            임시저장된 주문입니다. 이어서 작성해 완성하거나, 내용이 다 찼으면 바로 접수하세요.
          </p>
          {submitError ? (
            <div className={styles.error} role="alert">
              {submitError}
            </div>
          ) : null}
          <div className={styles.actions}>
            <button data-variant="primary" onClick={() => navigate(`/orders/${orderId}/edit`)} disabled={submitting}>
              이어서 작성
            </button>
            <button data-variant="secondary" onClick={() => void submitDraft()} disabled={submitting}>
              {submitting ? '접수 중…' : '바로 접수'}
            </button>
          </div>
        </div>
      ) : isCompany && order.infoRequest ? (
        <CompanyInfoRequestPanel order={order} onDone={reload} />
      ) : isCompany && (order.canCompanyEdit || order.canCompanyEditTextOnly) ? (
        <div className={styles.card} style={{ marginTop: 'var(--space-xl)' }}>
          <p className={styles.muted} style={{ marginBottom: 'var(--space-sm)' }}>
            {order.canCompanyEdit
              ? '위 항목을 눌러 문구·수취인·배송지 등을 바로 고칠 수 있어요. 취소는 전화로 문의해 주세요.'
              : '위 항목을 눌러 배송지·문구 등 텍스트를 바로 고칠 수 있어요(상품·수량 변경 불가, 변경 시 업체에 자동 통지). 상품·수량 변경이나 취소는 전화로 문의해 주세요.'}
          </p>
          {reorderError ? (
            <div className={styles.error} role="alert">
              {reorderError}
            </div>
          ) : null}
          <div className={styles.actions}>
            <button data-variant="primary" onClick={() => navigate(`/orders/${orderId}/edit`)}>
              정보 수정
            </button>
            {canReorder ? (
              <button data-variant="secondary" onClick={() => void reorder()} disabled={reordering}>
                {reordering ? '준비 중…' : '다시 주문'}
              </button>
            ) : null}
          </div>
          <CallCsLink block />
        </div>
      ) : isCompany ? (
        <div className={styles.card} style={{ marginTop: 'var(--space-xl)', borderStyle: 'dashed', background: 'var(--color-bg-page)' }}>
          <p className={styles.muted} style={{ margin: 0 }}>
            주문 변경·취소는 전화로 문의해 주세요. 화면에서는 상태를 바꿀 수 없습니다.
          </p>
          {reorderError ? (
            <div className={styles.error} role="alert">
              {reorderError}
            </div>
          ) : null}
          {canReorder ? (
            <div className={styles.actions} style={{ marginTop: 'var(--space-md)' }}>
              <button data-variant="primary" onClick={() => void reorder()} disabled={reordering}>
                {reordering ? '준비 중…' : '다시 주문'}
              </button>
            </div>
          ) : null}
          <CallCsLink block />
        </div>
      ) : null}

      {/* 인라인 수정 바텀시트(BIZ-76): 한 필드만 고쳐 본문 전체를 amend로 저장한다. */}
      {editing && canEdit ? (
        <OrderEditSheet
          order={order}
          fieldId={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      ) : null}
    </main>
  )
}

// ── 인라인 수정 바텀시트(BIZ-76) ───────────────────────────────

/**
 * 필드 하나만 편집하는 바텀시트. 현재 주문 본문 전체를 폼으로 되살린 뒤, 열린 필드만
 * 고쳐 buildOrderBody로 본문 전체를 다시 만들어 amendBody(/body)로 보낸다. 즉 위저드로
 * 고쳤을 본문과 규칙이 같다(단일 원천 orderForm). 상품·수량은 손대지 않는다.
 */
function OrderEditSheet({
  order,
  fieldId,
  onClose,
  onSaved,
}: {
  order: OrderDetailResponse
  fieldId: EditFieldId
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState<FormState>(() => orderToForm(order))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const set =
    (k: keyof FormState) =>
    (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [k]: e.target.value }))

  async function openAddr() {
    try {
      await openPostcodeSearch(({ address, zipcode }) =>
        setForm((f) => ({ ...f, deliveryAddress: address, deliveryZipcode: zipcode })),
      )
    } catch (e) {
      setErr(e instanceof Error ? e.message : '주소 검색을 열 수 없습니다')
    }
  }

  async function save() {
    if (!order.item) return
    setBusy(true)
    setErr('')
    // submit:true로 조립·검증해 필수 필드를 비우는 실수를 400으로 먼저 막는다(접수된 주문은 이미 필수가 차 있다).
    const body = buildOrderBody(order.occasionType, order.item.productId, form, true)
    // 장례식장 연결(BIZ-90): 장소명을 안 건드렸으면 기존 연결을 그대로 싣는다(동명 식장이라도 안 풀리게).
    if (order.funeralHall && form.deliveryPlaceName.trim() === (order.deliveryPlaceName ?? '').trim()) {
      body['funeralHallId'] = order.funeralHall.id
    }
    const parsed = createOrderRequest.safeParse(body)
    if (!parsed.success) {
      setErr(parsed.error.issues[0]?.message ?? '입력값을 확인하세요')
      setBusy(false)
      return
    }
    try {
      await ordersApi.amendBody(order.id, parsed.data)
      onSaved()
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '저장하지 못했습니다')
      setBusy(false)
    }
  }

  return (
    <BottomSheet
      title={SHEET_LABEL[fieldId]}
      onClose={onClose}
      footer={
        <button data-variant="primary" onClick={() => void save()} disabled={busy}>
          {busy ? '저장 중…' : '저장'}
        </button>
      }
    >
      {err ? (
        <div className={styles.error} role="alert">
          {err}
        </div>
      ) : null}
      <EditFields fieldId={fieldId} form={form} set={set} onAddr={() => void openAddr()} busy={busy} />
    </BottomSheet>
  )
}

/** 편집 대상 필드의 입력 UI. 대부분 단일 텍스트, 배송지는 주소 검색이 붙은 묶음, 희망일은 날짜. */
function EditFields({
  fieldId,
  form,
  set,
  onAddr,
  busy,
}: {
  fieldId: EditFieldId
  form: FormState
  set: (k: keyof FormState) => (e: { target: { value: string } }) => void
  onAddr: () => void
  busy: boolean
}) {
  if (fieldId === 'address') {
    return (
      <>
        <label className={styles.field}>
          <span className={styles.label}>배송지 주소</span>
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <input value={form.deliveryAddress} onChange={set('deliveryAddress')} disabled={busy} />
            <button type="button" data-variant="secondary" onClick={onAddr} disabled={busy} style={{ whiteSpace: 'nowrap' }}>
              주소 검색
            </button>
          </div>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>상세 주소 (선택)</span>
          <input value={form.deliveryAddressDetail} onChange={set('deliveryAddressDetail')} disabled={busy} />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>장소명 (장례식장·행사장, 선택)</span>
          <input value={form.deliveryPlaceName} onChange={set('deliveryPlaceName')} disabled={busy} />
        </label>
      </>
    )
  }

  if (fieldId === 'desiredDate') {
    return (
      <label className={styles.field}>
        <span className={styles.label}>{SHEET_LABEL.desiredDate}</span>
        <input type="date" value={form.desiredDate} onChange={set('desiredDate')} disabled={busy} />
      </label>
    )
  }

  // 그 밖은 폼 키와 이름이 같은 단일 텍스트 입력.
  const key = fieldId as keyof FormState
  return (
    <label className={styles.field}>
      <span className={styles.label}>{SHEET_LABEL[fieldId]}</span>
      <input value={form[key]} onChange={set(key)} disabled={busy} autoFocus />
    </label>
  )
}

// ── 기업 확인 요청 응답(BIZ-58) ────────────────────────────────

/**
 * 확인 필요(INFO_REQUIRED) 주문에서 운영자 요청 메시지를 보여주고, 기업이 응답하면
 * 재검토(UNDER_REVIEW)로 되돌린다. 현재 내부 상태는 기업에 감춰져(order.status=null)
 * infoRequest 유무로 렌더한다. 전이 허용·소유권은 서버가 최종 판정한다.
 */
function CompanyInfoRequestPanel({ order, onDone }: { order: OrderDetailResponse; onDone: () => void }) {
  const req = order.infoRequest
  const [response, setResponse] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function submit() {
    setBusy(true)
    setErr('')
    try {
      const body: OrderTransitionRequest = { to: 'UNDER_REVIEW', forced: false }
      if (response.trim()) body.reason = response.trim()
      await ordersApi.transition(order.id, body)
      onDone()
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '응답 전송에 실패했습니다')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.card} style={{ marginTop: 'var(--space-xl)' }}>
      <div className={styles.sectionTitle}>운영자 확인 요청</div>
      {req ? (
        <div
          className={styles.error}
          role="note"
          style={{ whiteSpace: 'pre-wrap', marginBottom: 'var(--space-md)' }}
        >
          {req.message}
          <div className={styles.muted} style={{ fontSize: 'var(--text-label)', marginTop: 6 }}>
            요청 시각 {fmtDateTime(req.requestedAt)}
          </div>
        </div>
      ) : null}
      <p className={styles.muted} style={{ marginTop: 0 }}>
        요청 내용을 확인하고 응답하면 운영자가 다시 검토합니다. 필요하면 응답에 정정 내용을 적어 주세요.
      </p>
      {err ? (
        <div className={styles.error} role="alert">
          {err}
        </div>
      ) : null}
      <label className={styles.field}>
        <span className={styles.label}>응답 내용 (선택)</span>
        <textarea
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          maxLength={500}
          rows={3}
          disabled={busy}
          placeholder="예: 배송지를 정정했습니다 / 요청하신 정보는 …"
        />
      </label>
      <div className={styles.actions}>
        <button data-variant="primary" onClick={() => void submit()} disabled={busy}>
          {busy ? '전송 중…' : '응답 완료 · 재검토 요청'}
        </button>
      </div>
    </div>
  )
}

// ── 업체 상태 전이 ─────────────────────────────────────────────

/**
 * 업체용 상태 전이 패널. 허용 전이는 상태머신(spec의 TRANSITIONS)에서 현재 상태·업체
 * 역할로 계산한다 — 규칙을 화면에 다시 쓰지 않는다. 서버가 최종 판정한다.
 * 운영자 패널과 달리 업체엔 배정·취소비용·강제전이가 없어 사유 입력만 다룬다.
 */
/**
 * 입력이 필요해 패널을 펼쳐야 하는 업체 전이인지. 사유 필수이거나 배송 시작(기사 정보 선택 입력)이면 참.
 * 그 외(주문 접수·상품 준비·배송 완료 처리)는 버튼 한 번에 바로 실행한다.
 */
function vendorNeedsInputPanel(t: Transition): boolean {
  return t.requiresReason === true || t.to === 'DELIVERING'
}

function VendorActionPanel({
  order,
  onApplied,
}: {
  order: OrderDetailResponse
  onApplied: (order: OrderDetailResponse) => void
}) {
  const from = order.status as OrderStatus
  const allowed = useMemo(
    () => TRANSITIONS.filter((t) => t.from === from && t.actors.includes('VENDOR_MEMBER')),
    [from],
  )

  // 할 수 있는 처리가 없을 때, "왜 비었는지"를 상태에 맞게 말해준다 —
  // 밋밋한 "처리가 없습니다"보다 완료/반려/대기를 구분해 안심시킨다.
  const emptyMessage =
    from === 'DELIVERED'
      ? '배송까지 완료된 주문이에요.'
      : from === 'VENDOR_REJECTED'
        ? '접수를 반려한 주문이에요.'
        : from === 'CANCELLED'
          ? '취소된 주문이에요.'
          : '지금은 처리할 단계가 아니에요. 다음 진행을 기다리고 있어요.'

  const [active, setActive] = useState<Transition | null>(null)
  const [reason, setReason] = useState('')
  const [driverName, setDriverName] = useState('')
  const [driverPhone, setDriverPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [pendingTo, setPendingTo] = useState<OrderStatus | null>(null)
  const [err, setErr] = useState('')

  function openTransition(t: Transition) {
    setActive(t)
    setReason('')
    setDriverName('')
    setDriverPhone('')
    setErr('')
  }

  // 입력 없는 전이: 버튼 클릭 즉시 실행(확인 단계 없음).
  function directRun(to: OrderStatus) {
    setActive(null)
    setErr('')
    void run({ to, forced: false })
  }

  function confirmTransition() {
    if (!active) return
    if (active.requiresReason && !reason.trim()) {
      setErr('사유를 입력하세요')
      return
    }
    const body: OrderTransitionRequest = { to: active.to, forced: false }
    if (reason.trim()) body.reason = reason.trim()
    // 배송 시작 시 기사 정보(선택). 시작 시각은 서버가 굳힌다(BIZ-24).
    if (active.to === 'DELIVERING') {
      if (driverName.trim()) body.driverName = driverName.trim()
      if (driverPhone.trim()) body.driverPhone = driverPhone.trim()
    }
    void run(body)
  }

  async function run(body: OrderTransitionRequest) {
    setBusy(true)
    setPendingTo(body.to)
    setErr('')
    try {
      const res = await ordersApi.transition(order.id, body)
      setActive(null)
      onApplied(res.order) // 재요청 없이 전이 응답으로 즉시 반영
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '전이에 실패했습니다')
    } finally {
      setBusy(false)
      setPendingTo(null)
    }
  }

  return (
    <div className={styles.card} style={{ marginTop: 'var(--space-xl)' }}>
      <div className={styles.sectionTitle}>주문 처리</div>
      {err ? (
        <div className={styles.error} role="alert">
          {err}
        </div>
      ) : null}

      <div className={styles.actions} style={{ flexWrap: 'wrap', marginTop: 0 }}>
        {allowed.length === 0 ? (
          <span className={styles.muted}>{emptyMessage}</span>
        ) : (
          allowed.map((t) => (
            <button
              key={t.to}
              data-variant={active?.to === t.to ? 'primary' : 'secondary'}
              onClick={() => (vendorNeedsInputPanel(t) ? openTransition(t) : directRun(t.to))}
              disabled={busy}
            >
              {pendingTo === t.to ? '처리 중…' : (VENDOR_TRANSITION_LABEL[t.to] ?? t.to)}
            </button>
          ))
        )}
      </div>

      {active ? (
        <div style={{ marginTop: 'var(--space-lg)', maxWidth: 480 }}>
          <div style={{ marginBottom: 'var(--space-sm)', fontWeight: 'var(--weight-medium)' }}>
            {VENDOR_TRANSITION_LABEL[active.to] ?? active.to}
          </div>
          <label className={styles.field}>
            <span className={styles.label}>사유 {active.requiresReason ? '(필수)' : '(선택)'}</span>
            <input maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
          </label>
          {active.to === 'DELIVERING' ? (
            <>
              <label className={styles.field}>
                <span className={styles.label}>기사명 (선택)</span>
                <input maxLength={100} value={driverName} onChange={(e) => setDriverName(e.target.value)} disabled={busy} />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>기사 연락처 (선택)</span>
                <input maxLength={30} value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} disabled={busy} />
              </label>
            </>
          ) : null}
          <div className={styles.actions}>
            <button data-variant="primary" onClick={confirmTransition} disabled={busy}>
              {busy ? '처리 중…' : '확인'}
            </button>
            <button data-variant="secondary" onClick={() => setActive(null)} disabled={busy}>
              닫기
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ── 처리 이력 ──────────────────────────────────────────────────

/**
 * 처리 이력 타임라인 (BIZ-27). 라벨·부가설명은 서버가 역할에 맞게 렌더한다 —
 * 기업엔 파생 상태만, 업체엔 발주 이후 단계만. 화면은 받은 걸 그대로 그린다.
 */
function HistorySection({ orderId }: { orderId: number }) {
  const { data, loading, error } = useAsync(() => ordersApi.history(orderId), [orderId])
  const events = data?.events ?? []

  return (
    <section className={styles.infoCard} style={{ marginTop: 'var(--space-lg)' }}>
      <CardTitle icon={ICON.history}>처리 이력</CardTitle>
      {loading ? (
        <p className={styles.muted}>불러오는 중…</p>
      ) : error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : events.length === 0 ? (
        <p className={styles.muted}>처리 이력이 없습니다.</p>
      ) : (
        <ol className={styles.history}>
          {events.map((e, i) => (
            <li key={i} className={styles.historyItem}>
              {/* 서버는 시간 오름차순(가장 오래된 것부터)으로 준다 — 채운 점은 마지막(최신) 이벤트. */}
              <span className={`${styles.historyDot} ${i === events.length - 1 ? '' : styles.historyDotMuted}`} />
              <div className={styles.historyTitle}>
                {e.label}
                {e.forced ? (
                  <span className={`${styles.badge} ${styles.chipWarning}`} style={{ marginLeft: 'var(--space-sm)' }}>
                    강제
                  </span>
                ) : null}
              </div>
              <div className={styles.historyWhen}>{fmtDateTime(e.at)}</div>
              {e.sublabel ? <div className={styles.historySub}>{e.sublabel}</div> : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

// ── 배송 사진 ──────────────────────────────────────────────────

/**
 * 배송 사진 섹션. 기업·업체·운영자 모두 조회한다(범위는 서버가 건다).
 * 업로드는 업체이고 주문이 배송 단계일 때만 노출한다(서버도 막지만 UX).
 */
function PhotosSection({
  orderId,
  order,
  isVendor,
}: {
  orderId: number
  order: OrderDetailResponse
  isVendor: boolean
}) {
  const { data, loading, error, reload } = useAsync(() => photosApi.list(orderId), [orderId])
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const canUpload =
    isVendor && order.status !== null && PHOTO_UPLOAD_STATES.includes(order.status as OrderStatus)

  async function handleFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    e.target.value = '' // 같은 파일을 다시 골라도 change가 발생하도록 비운다
    if (files.length === 0) return
    setUploadError('')
    setUploading(true)
    try {
      // 여러 장은 한 장씩 순차 업로드한다(서버 엔드포인트는 단건 multipart).
      for (const file of files) {
        await photosApi.upload(orderId, file)
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : '업로드에 실패했습니다')
    } finally {
      reload() // 일부만 성공해도 올라간 만큼 반영
      setUploading(false)
    }
  }

  async function handleDelete(photoId: number) {
    if (!window.confirm('이 사진을 삭제할까요?')) return
    setUploadError('')
    setDeletingId(photoId)
    try {
      await photosApi.delete(orderId, photoId)
      reload()
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : '삭제에 실패했습니다')
    } finally {
      setDeletingId(null)
    }
  }

  const photos: PhotoView[] = data?.photos ?? []

  return (
    <section className={styles.infoCard} style={{ marginTop: 'var(--space-lg)' }}>
      <CardTitle
        icon={ICON.photo}
        right={
          canUpload ? (
            <button data-variant="primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? '업로드 중…' : '사진 업로드'}
            </button>
          ) : null
        }
      >
        배송 사진
      </CardTitle>

      {canUpload ? (
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => void handleFiles(e)}
          disabled={uploading}
          style={{ display: 'none' }}
        />
      ) : null}

      {uploadError ? (
        <div className={styles.error} role="alert">
          {uploadError}
        </div>
      ) : null}

      {loading ? (
        <p className={styles.muted}>불러오는 중…</p>
      ) : error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : photos.length === 0 ? (
        <p className={styles.muted}>등록된 사진 없음</p>
      ) : (
        <div className={styles.gallery}>
          {photos.map((photo) => (
            <div key={photo.id} className={styles.galleryItem} style={{ position: 'relative' }}>
              <a href={photo.displayUrl} target="_blank" rel="noreferrer">
                <img src={photo.thumbUrl} alt={`배송 사진 ${fmtDateTime(photo.createdAt)}`} />
              </a>
              {canUpload ? (
                <button
                  type="button"
                  onClick={() => void handleDelete(photo.id)}
                  disabled={deletingId === photo.id}
                  aria-label="사진 삭제"
                  title="사진 삭제"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    border: 'none',
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 0, 0.55)',
                    color: '#fff',
                    fontSize: 16,
                    lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >
                  {deletingId === photo.id ? '…' : '×'}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * 결제 배너 (BIZ-78). 가승인(선불만) 기업 주문에서 서버 판정(paymentRequired)에 따라
 * 결제를 유도하고, 완료·입금대기 상태를 보여준다. 결제창 성공 시 successUrl로 떠나므로
 * confirm은 /payments/success 페이지가 처리한다.
 */
function PaymentBanner({ order }: { order: OrderDetailResponse }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const p = order.payment
  const paid = p?.status === 'DONE' || p?.status === 'PARTIAL_CANCELED'
  const waiting = p?.status === 'WAITING_DEPOSIT'
  if (!order.paymentRequired && !paid && !waiting) return null

  const amount = p?.amount ?? order.item?.totalAmount ?? 0

  async function pay() {
    setBusy(true)
    setError('')
    try {
      const { payment } = await paymentsApi.create(order.id)
      await startTossPayment({
        orderId: order.id,
        orderKey: payment.orderKey,
        amount: payment.amount,
        orderName: `${order.item?.productName ?? '화환'} 주문`,
      })
      // 성공하면 페이지가 토스 successUrl로 떠난다 — 여기 도달하는 건 창이 닫힌 경우뿐.
      setBusy(false)
    } catch (err) {
      if (!isTossUserCancel(err)) {
        setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : '결제를 시작하지 못했습니다')
      }
      setBusy(false)
    }
  }

  const box: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    flexWrap: 'wrap',
    margin: 'var(--space-md) 0',
    padding: '16px 18px',
    borderRadius: 'var(--round-card)',
  }

  if (paid) {
    return (
      <div style={{ ...box, background: 'var(--surface-feature)', border: '1px solid var(--green-soft)' }}>
        <span style={{ fontWeight: 700, color: 'var(--green-dark)' }}>✓ 결제 완료</span>
        <span style={{ color: 'var(--steel)', fontSize: 14 }}>
          {amount.toLocaleString('ko-KR')}원{p?.method ? ` · ${p.method}` : ''}
          {p && p.canceledAmount > 0 ? ` (부분취소 ${p.canceledAmount.toLocaleString('ko-KR')}원)` : ''}
        </span>
      </div>
    )
  }
  if (waiting) {
    return (
      <div style={{ ...box, background: 'var(--warn-bg)', border: '1px solid transparent' }}>
        <span style={{ fontWeight: 700, color: 'var(--warn-text)' }}>가상계좌 입금 대기</span>
        <span style={{ color: 'var(--warn-text)', fontSize: 14 }}>
          {amount.toLocaleString('ko-KR')}원 입금이 확인되면 준비가 시작됩니다.
        </span>
      </div>
    )
  }
  return (
    <div style={{ ...box, background: 'var(--surface-feature)', border: '1px solid var(--green-soft)' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, color: 'var(--ink)' }}>결제가 필요해요</div>
        <div style={{ color: 'var(--steel)', fontSize: 14, marginTop: 3 }}>
          결제가 완료되면 주문 준비가 시작됩니다.
        </div>
        {error ? (
          <div role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginTop: 6 }}>
            {error}
          </div>
        ) : null}
      </div>
      <button type="button" className="btn" onClick={() => void pay()} disabled={busy} style={{ minHeight: 48 }}>
        {busy ? '결제창 여는 중…' : `${amount.toLocaleString('ko-KR')}원 결제하기`}
      </button>
    </div>
  )
}
